import DateFieldPicker from "components/shared/DateFieldPicker";
import { StyledMenuItem, StyledSelect } from "components/shared/StyledSelect";
import type { ITaxProfile } from "helpers/beneficiaryDocuments";
import {
    expectedTypes,
    EXPIRING_SOON_DAYS,
    fromDateOnly,
    isCompleteDateOnly,
    MIN_DOCUMENT_YEAR,
    toDateOnly,
} from "helpers/beneficiaryDocuments";
import { BodyHighlight, SmallBody } from "helpers/TypographyComponents";
import { useAbility } from "hooks/useAbility";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import type { IBeneficiaryDocumentType } from "store/beneficiaryDocumentTypesStore";

export interface IDocumentTypeFieldsValue {
    documentTypeId: number | null;
    /** YYYY-MM-DD */
    validFrom: string | null;
    validUntil: string | null;
}

export const emptyDocumentTypeFields: IDocumentTypeFieldsValue = {
    documentTypeId: null,
    validFrom: null,
    validUntil: null,
};

/** Dates required when the type asks for them, and in the right order. */
export const isDocumentTypeFieldsValid = (
    value: IDocumentTypeFieldsValue,
    documentTypes: IBeneficiaryDocumentType[],
) => {
    const type = documentTypes.find((documentType) => documentType.id === value.documentTypeId);
    if (type?.requiresValidity && (!value.validFrom || !value.validUntil)) return false;
    if (
        (value.validFrom && !isCompleteDateOnly(value.validFrom)) ||
        (value.validUntil && !isCompleteDateOnly(value.validUntil))
    )
        return false;
    if (value.validFrom && value.validUntil && value.validFrom > value.validUntil) return false;

    return true;
};

/** Appends the fields to a multipart upload (empty strings are read as null by the API). */
export const appendDocumentTypeFields = (formData: FormData, value: IDocumentTypeFieldsValue) => {
    formData.append("documentTypeId", value.documentTypeId ? String(value.documentTypeId) : "");
    formData.append("validFrom", value.validFrom ?? "");
    formData.append("validUntil", value.validUntil ?? "");
};

interface IDocumentTypeFields {
    documentTypes: IBeneficiaryDocumentType[];
    value: IDocumentTypeFieldsValue;
    onChange: (value: IDocumentTypeFieldsValue) => void;
    /** Hide the "other document" choice (e.g. when qualifying a pending upload) */
    requireType?: boolean;
    /** When given, the types expected from them come first and are flagged, and the hint lists them */
    beneficiary?: ITaxProfile;
}

const NONE = "none";

/** Document type + validity period, shared by the upload dialog and the qualification modal. */
const DocumentTypeFields = ({
    documentTypes,
    value,
    onChange,
    requireType = false,
    beneficiary,
}: IDocumentTypeFields) => {
    const [t] = useTranslation();
    const selectedType = documentTypes.find((type) => type.id === value.documentTypeId);
    // Expected types first, flagged in the list and named in the hint
    const expected = expectedTypes(documentTypes, beneficiary);
    const orderedTypes = [...expected, ...documentTypes.filter((type) => !expected.includes(type))];
    const ability = useAbility();
    // The settings tab only exists for roles allowed to customise the beneficiaries settings
    const canOpenSettings = ability.can("access", "CustomBeneficiarySettings");
    const minDate = fromDateOnly(`${MIN_DOCUMENT_YEAR}-01-01`) ?? undefined;

    return (
        <>
            <div className="flex flex-col gap-2">
                <BodyHighlight color="black">{t("BeneficiaryDocuments.Documents.TypeOfDocument")}</BodyHighlight>
                <StyledSelect
                    fullWidth
                    value={value.documentTypeId ?? NONE}
                    onChange={(e) =>
                        onChange({
                            ...value,
                            documentTypeId: e.target.value === NONE ? null : Number(e.target.value),
                        })
                    }
                >
                    {orderedTypes.map((type) => (
                        <StyledMenuItem key={type.id} value={type.id}>
                            {type.name}
                            {expected.includes(type) && (
                                <SmallBody tag="span" color="grey">
                                    {`\u00a0· ${t("BeneficiaryDocuments.Documents.Expected")}`}
                                </SmallBody>
                            )}
                        </StyledMenuItem>
                    ))}
                    {!requireType && (
                        <StyledMenuItem value={NONE}>
                            {t("BeneficiaryDocuments.Documents.OtherDocumentOption")}
                        </StyledMenuItem>
                    )}
                </StyledSelect>
                {beneficiary && (
                    <SmallBody color="grey" className="[&_a]:text-info [&_a]:underline">
                        {expected.length
                            ? t("BeneficiaryDocuments.Documents.ExpectedForBeneficiary", {
                                  types: expected.map((type) => type.name).join(", "),
                              })
                            : t("BeneficiaryDocuments.Documents.NoneExpectedForBeneficiary")}
                        {canOpenSettings && (
                            <>
                                {" "}
                                {t("BeneficiaryDocuments.Documents.TypesDefinedIn")}{" "}
                                <Link to="/settings/beneficiary-documents">
                                    {t("BeneficiaryDocuments.Documents.SettingsLink")}
                                </Link>
                                .
                            </>
                        )}
                    </SmallBody>
                )}
            </div>
            {selectedType && (
                <div className="flex flex-col gap-2">
                    <BodyHighlight color="black">
                        {t("BeneficiaryDocuments.Documents.ValidityPeriod")}
                        {selectedType.requiresValidity && (
                            <BodyHighlight tag="span" color="danger">
                                {" *"}
                            </BodyHighlight>
                        )}
                    </BodyHighlight>
                    <div className="flex flex-wrap gap-6">
                        <div className="flex flex-col gap-1">
                            <SmallBody color="grey">{t("BeneficiaryDocuments.Documents.Start")}</SmallBody>
                            <DateFieldPicker
                                value={fromDateOnly(value.validFrom)}
                                onChange={(date: Date | null) => onChange({ ...value, validFrom: toDateOnly(date) })}
                                minDate={minDate}
                            />
                        </div>
                        <div className="flex flex-col gap-1">
                            <SmallBody color="grey">{t("BeneficiaryDocuments.Documents.End")}</SmallBody>
                            <DateFieldPicker
                                value={fromDateOnly(value.validUntil)}
                                onChange={(date: Date | null) => onChange({ ...value, validUntil: toDateOnly(date) })}
                                minDate={fromDateOnly(value.validFrom) ?? minDate}
                            />
                        </div>
                    </div>
                    {selectedType.requiresValidity && (
                        <SmallBody color="grey">
                            {t("BeneficiaryDocuments.Documents.ValidityHelp", { days: EXPIRING_SOON_DAYS })}
                        </SmallBody>
                    )}
                </div>
            )}
        </>
    );
};

export default DocumentTypeFields;
